import { fnv1a32 } from "@bindery/shared/std/seeded-random";
import * as z from "zod";
import { bookPath, type BookData, bookDataSchema } from "@/contracts/book";
import { readingPath } from "@/contracts/reading";
import { parseBookshopAffiliateUrl } from "@/ear-reader/affiliate";

export const HEADLINE_TEST = "affiliate_headline";
export const HEADLINES = {
  A: "Curl up with this book",
  B: "Add this to your bookshelf",
  C: "Your next bedtime story",
  D: "A book to call your own",
} as const;
export type HeadlineVariant = keyof typeof HEADLINES;
const VARIANTS = Object.keys(HEADLINES) as HeadlineVariant[];

// FNV-1a, as ../app bucketed, so a reader keeps the variant they had there.
export function headlineVariant(seed: string): HeadlineVariant {
  return VARIANTS[fnv1a32(seed) % VARIANTS.length] ?? "A";
}

export type AffiliateLoad =
  | { kind: "ready"; affiliateId: string; sku: string; bookData: BookData }
  | { kind: "unavailable"; reason: AffiliateUnavailable };

export type AffiliateUnavailable =
  | "reading_failed"
  | "no_affiliate_source"
  | "book_failed";

export interface AffiliateDeps {
  fetch: (input: string, init?: RequestInit) => Promise<Response>;
  token: () => Promise<string | null>;
}

const readingSourceSchema = z.object({ source: z.string().optional() });

async function readJson(response: Response): Promise<unknown> {
  if (!response.ok) return null;
  try {
    return await response.json();
  } catch {
    return null;
  }
}

// The affiliate link comes only from the reading's CDN `source`, never the
// page's URL: a crafted link must not choose where a purchase is credited.
export async function loadAffiliate(
  readingId: string,
  deps: AffiliateDeps,
): Promise<AffiliateLoad> {
  const reading = readingSourceSchema.safeParse(
    await readJson(await deps.fetch(readingPath(readingId))),
  );
  if (!reading.success)
    return { kind: "unavailable", reason: "reading_failed" };
  const affiliate =
    reading.data.source === undefined
      ? null
      : parseBookshopAffiliateUrl(reading.data.source);
  if (affiliate === null) {
    return { kind: "unavailable", reason: "no_affiliate_source" };
  }

  const token = await deps.token();
  const book = bookDataSchema.safeParse(
    await readJson(
      await deps.fetch(bookPath(affiliate.sku, affiliate.affiliateId), {
        headers: token === null ? {} : { authorization: `Bearer ${token}` },
      }),
    ),
  );
  if (!book.success) return { kind: "unavailable", reason: "book_failed" };
  return {
    kind: "ready",
    affiliateId: affiliate.affiliateId,
    sku: affiliate.sku,
    bookData: book.data,
  };
}
